import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Grid,
  Card,
  CardMedia,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { uploadImages } from '../services/api';

const MotionPaper = motion(Paper);

const Upload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file),
      status: 'pending'
    }));

    setFiles(prev => [...prev, ...newFiles]);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp']
    },
    multiple: true,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const removeFile = (fileId) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return updated;
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const filesToUpload = files.map(f => f.file);
      const results = await uploadImages(filesToUpload);

      setUploadResults(results);
      setFiles([]);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const clearResults = () => {
    setUploadResults([]);
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          mb: 2,
          textAlign: 'center',
          background: 'linear-gradient(45deg, #667eea, #764ba2)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 600,
        }}
      >
        Upload Photos
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 4,
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.7)',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        Upload your photos to let AI analyze and understand them.
        The system will automatically extract tags, objects, text, and colors for smart searching.
      </Typography>

      {/* Dropzone */}
      <MotionPaper
        {...getRootProps()}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        sx={{
          p: 6,
          mb: 4,
          textAlign: 'center',
          cursor: 'pointer',
          background: isDragActive
            ? 'rgba(102, 126, 234, 0.1)'
            : 'rgba(26, 26, 46, 0.8)',
          backdropFilter: 'blur(10px)',
          border: isDragActive
            ? '2px dashed #667eea'
            : '2px dashed rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: '#667eea',
            background: 'rgba(102, 126, 234, 0.05)',
          }
        }}
      >
        <input {...getInputProps()} />

        <UploadIcon
          sx={{
            fontSize: 64,
            color: isDragActive ? '#667eea' : 'rgba(255, 255, 255, 0.5)',
            mb: 2
          }}
        />

        <Typography
          variant="h6"
          sx={{
            mb: 1,
            color: isDragActive ? '#667eea' : 'rgba(255, 255, 255, 0.9)'
          }}
        >
          {isDragActive ? 'Drop images here...' : 'Drag & drop images here'}
        </Typography>

        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            mb: 2
          }}
        >
          or click to select files
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.5)'
          }}
        >
          Supported formats: JPEG, PNG, GIF, WebP, BMP (max 50MB each)
        </Typography>
      </MotionPaper>

      {/* File Preview */}
      {files.length > 0 && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            background: 'rgba(26, 26, 46, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
            Selected Files ({files.length})
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {files.map((fileObj) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={fileObj.id}>
                <Card
                  sx={{
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <CardMedia
                    component="img"
                    height="150"
                    image={fileObj.preview}
                    alt={fileObj.file.name}
                    sx={{ objectFit: 'cover' }}
                  />

                  <IconButton
                    size="small"
                    onClick={() => removeFile(fileObj.id)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.8)',
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {fileObj.file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                    >
                      {(fileObj.file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              sx={{
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                px: 4,
                py: 1.5,
              }}
            >
              {uploading ? 'Uploading...' : `Upload ${files.length} Image${files.length > 1 ? 's' : ''}`}
            </Button>

            <Button
              variant="outlined"
              onClick={() => setFiles([])}
              disabled={uploading}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                }
              }}
            >
              Clear All
            </Button>
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  }
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  display: 'block',
                  textAlign: 'center',
                  mt: 1
                }}
              >
                AI is analyzing your images...
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <Paper
          sx={{
            p: 3,
            background: 'rgba(26, 26, 46, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              Upload Results
            </Typography>
            <Button onClick={clearResults} size="small">
              Clear Results
            </Button>
          </Box>

          <List>
            {uploadResults.map((result, index) => (
              <ListItem
                key={index}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: 1,
                  mb: 1,
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                }}
              >
                <ListItemIcon>
                  {result.error ? (
                    <ErrorIcon sx={{ color: '#f44336' }} />
                  ) : (
                    <CheckIcon sx={{ color: '#4caf50' }} />
                  )}
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      {result.filename || `Image ${index + 1}`}
                    </Typography>
                  }
                  secondary={
                    result.error ? (
                      <Typography sx={{ color: '#f44336' }}>
                        {result.error}
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                          ID: {result.id}
                        </Typography>
                        {result.metadata?.description && (
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                            AI found: {result.metadata.description}
                          </Typography>
                        )}
                        {result.metadata?.google_vision_labels && result.metadata.google_vision_labels.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {result.metadata.google_vision_labels.slice(0, 5).map((label, idx) => (
                              <Chip
                                key={idx}
                                label={label}
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 20,
                                  backgroundColor: 'rgba(102, 126, 234, 0.2)',
                                  color: 'rgba(255, 255, 255, 0.8)',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default Upload;